import { isUndefined, min } from 'lodash/fp';
import React, { useMemo } from 'react';
import classnames from 'classnames';

import { ARROW_WIDTH_OFFSET, Carousel } from '../../../shared/carousel/Carousel';
import { Add, Document, MultiAnalytics, Search, Table } from '../../../shared/icons';
import { HomeButtonTile, HomeButtonTileAdd } from '../HomeButton';
import styles from '../Modules.module.scss';
import { DocumentSpreadChart } from './DocumentSpeadChart';
import { EntitySpreadChart } from './EntitySpreadChart';
import { EntitySpreadMap } from './EntitySpreadMap';
import { HomeRiskTolerance } from './HomeRiskTolerance';
import { useSystemColourPreference } from '../../../../hooks/useSystemColourPreference';
import { useAppSelector } from '../../../../hooks/react-redux';
import { getUserHasFeaturePermission } from '../../../auth/login/store';
import { FeaturePermission } from '../../../admin/users/store';
import { HomePageLink } from '../../../admin/system-maintenance/store';

const { lightestGrey } = styles;

const CAROUSEL_TIMEOUT = 15000; // 15 seconds
const TITLE_HEIGHT = 51;
const MODULE_PADDING = 20;

interface DocumentsModuleProps {
    dimensions?: {
        height: number;
        width: number;
    }
    analyticsSpinner: boolean;
    shouldDoubleWidth: boolean;
    reduceMapHeight: boolean;
    internetGatewayOpen: boolean;
    tileDisabledTooltip: (tile: HomePageLink) => string | undefined;
}

export const DocumentsModule: React.FC<DocumentsModuleProps> = ({ dimensions, analyticsSpinner, shouldDoubleWidth, reduceMapHeight, internetGatewayOpen, tileDisabledTooltip }) => {
    const showDarkTheme = useSystemColourPreference();
    const hasUploadDocumentPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.UPLOAD_DOCUMENTS]));

    const chartDimensions = useMemo(() => {
        if (isUndefined(dimensions)) {
            return { height: 0, width: 0 };
        }
        return { height: dimensions.height - TITLE_HEIGHT, width: ((dimensions.width / (shouldDoubleWidth ? 1 : 2)) - (ARROW_WIDTH_OFFSET + MODULE_PADDING)) };
    }, [dimensions, shouldDoubleWidth]);

    const addDocumentsDisabled = useMemo(() => !hasUploadDocumentPermission || internetGatewayOpen || !isUndefined(tileDisabledTooltip(HomePageLink.ADD_DOCUMENTS)), [hasUploadDocumentPermission, internetGatewayOpen, tileDisabledTooltip]);

    const rotatingCharts = useMemo(() => [
        <DocumentSpreadChart dimensions={chartDimensions} analyticsSpinner={analyticsSpinner} key='documents' />,
        <EntitySpreadChart dimensions={chartDimensions} analyticsSpinner={analyticsSpinner} key='entities' />,
        <HomeRiskTolerance key='risk-tolerance' dimensions={dimensions} shouldDoubleWidth={shouldDoubleWidth} />,
        <EntitySpreadMap key='entities-map' dimensions={chartDimensions} reduceMapHeight={reduceMapHeight} analyticsSpinner={analyticsSpinner} />
    ], [chartDimensions, analyticsSpinner, shouldDoubleWidth, dimensions, reduceMapHeight]);

    const iconSize = useMemo(() => min([chartDimensions.height / 5, chartDimensions.width / ((shouldDoubleWidth ? 2 : 4))]), [chartDimensions, shouldDoubleWidth]);

    const chartDisplay = useMemo(() => {
        if (chartDimensions.height > chartDimensions.width * 3) {
            return rotatingCharts;
        }
        return (
            <Carousel
                markers={[0, 1, 2, 3]}
                height={chartDimensions.height}
                width={chartDimensions.width}
                timeout={CAROUSEL_TIMEOUT}
            >
                {rotatingCharts}
            </Carousel>
        );
    }, [rotatingCharts, chartDimensions]);

    return (
        <div className={styles.moduleWrapper}>
            <div className={classnames(styles.titleWrapper, { [styles.darkThemeTitleWrapper]: showDarkTheme })}>Documents</div>
            <div className={styles.contentWrapper}>
                <div className={styles.menuOptionsWrapper}>
                    <div className={styles.shortcutMenuOptions}>
                        <HomeButtonTileAdd label='Upload' icon={Add} testId='upload-document' iconSize={iconSize} disabled={addDocumentsDisabled} disabledTooltip={tileDisabledTooltip(HomePageLink.ADD_DOCUMENTS)} />
                        <HomeButtonTile path='documents/my-documents' label='My Documents' icon={Document} testId='my-documents' iconSize={iconSize} fill={lightestGrey} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.MY_DOCUMENTS))} disabledTooltip={tileDisabledTooltip(HomePageLink.MY_DOCUMENTS)} />
                        <HomeButtonTile path='documents/search' label='Search' icon={Search} testId='search-documents' iconSize={iconSize} fill={lightestGrey} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.SEARCH_DOCUMENTS))} disabledTooltip={tileDisabledTooltip(HomePageLink.SEARCH_DOCUMENTS)} />
                        <HomeButtonTile path='documents/reporting' label='Reporting' icon={Table} testId='documents-reporting' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.DOCUMENT_REPORTING))} disabledTooltip={tileDisabledTooltip(HomePageLink.DOCUMENT_REPORTING)} />
                        <HomeButtonTile path='documents/analytics' label='Analytics' icon={MultiAnalytics} testId='documents-analytics' iconSize={iconSize} disabled={!isUndefined(tileDisabledTooltip(HomePageLink.DOCUMENT_ANALYTICS))} disabledTooltip={tileDisabledTooltip(HomePageLink.DOCUMENT_ANALYTICS)} />
                    </div>
                </div>
                <div className={styles.carouselChartsWrapper}>
                    {chartDisplay}
                </div>
            </div>
        </div>
    );
};
